import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { lockScroll, unlockScroll } from '@theme/utilities';

/**
 * SDM — floating menu ("burger"): a round glass button at the bottom of the page that opens a glass drawer
 * listing a menu (sections/sdm-burger.liquid). After the navigation button of travisscott.com.
 *
 * - It shows once its place is known and the preloader has gone, straight at its place (entrance animation). In
 *   the home page's experience it waits for the experience's interface, which brings the header in, and comes
 *   in a moment after it, slowly.
 * - On the home page, while the labyrinth's experience fills the screen, it sits under the header: a way out
 *   for whoever has not found that the page scrolls. It moves to the bottom (fades out, then in again there)
 *   once the experience's screen is half scrolled past, and back up on the way back.
 * - The button reads the background under it and switches between a light and a dark glass, so it stays
 *   legible over the labyrinth, the concert section or a light page without knowing about them.
 * - It gets out of the way (shrinks away) while a bar takes its place at the bottom of the screen: the
 *   product sheet's purchase bar on mobile, the cookie banner. On a product sheet on mobile it only comes in
 *   at the bottom of the page, over the footer, so that it never covers the sheet's buttons. Buying comes
 *   first.
 * - The glass is the home page's (sdm/glass from the import map): SVG refraction on Chromium desktop, blur
 *   elsewhere. Without it (another theme), the CSS blur stays.
 * - While the drawer is open: focus is kept inside, the page does not scroll, Escape or a click outside
 *   closes it, and the labyrinth behind does not react to swipes or arrow keys.
 * - The link set as `data-assistance` (the contact page) opens the Zendesk window instead; its page stays
 *   the fallback when Zendesk is not there (blocked, not loaded). The page keeps its place under the window,
 *   whoever opens it (keepScrollUnderZendesk).
 * - A link to a section of the page being shown (an anchor, /#packs) scrolls there, smoothly, instead of loading
 *   the page again.
 * - After a jump to a section (arriving with an anchor, following a link to one, from the drawer or not), it
 *   gets out of the way of a button that the jump brings under it: where the section lands depends on the
 *   screen's height and the browser's bars. It shrinks away from the start of the jump and comes back once
 *   that button has left its place.
 * - The pushes under the rows (pictures, films) load when the drawer is about to open — the pointer over the
 *   button, the focus on it, at the latest the opening — not with the page. Their films play while it is open
 *   (never by themselves with reduced motion), each with its pause button.
 * - Each push is measured (measurePush): seen — at least half of it in the open drawer, once a page — and followed.
 *
 * Dispatches `sdm:burger-open` and `sdm:burger-close` on the document.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} bouton - The round button (opens and closes the drawer).
 * @property {HTMLElement} tiroir - The drawer (`<nav>`), inert while closed.
 * @property {HTMLElement} voile - The veil over the page while the drawer is open.
 * @property {SVGDefsElement} [defs] - Where sdm/glass puts its refraction filters.
 *
 * @extends {Component<Refs>}
 */
class SdmBurger extends Component {
  requiredRefs = ['bouton', 'tiroir', 'voile'];

  /** @type {AbortController | undefined} */
  #abort;

  /** @type {MutationObserver | undefined} */
  #mutations;

  /** @type {IntersectionObserver | undefined} */
  #experience;

  /** Pending measure (bars, tone), one per frame. */
  #frame = 0;

  /** Whether the open drawer holds the focus and the page scroll (not when opened from the editor). */
  #modal = false;

  /** Height of Shopify's preview bar under the button, in px (theme previews only). */
  #apercu = 0;

  /** Whether the button belongs under the header (the home page's experience fills the screen). */
  #haut = false;

  /** Pending change of place (the button fading out). */
  #bascule = 0;

  /** Pending arrival in the experience, a moment after its interface. */
  #arrivee = 0;

  /** @type {MutationObserver | undefined} Watches the labyrinth for its interface (`sdm-hud-pret`). */
  #interface;

  /**
   * The section jumped to, while the button may give way to a button of the page there; `arrivee` once the jump
   * is over (the page there, or still for a moment), and `repos` the section's top then.
   * @type {{ element: Element, arrivee: boolean, repos: number | null } | null}
   */
  #ancre = null;

  /** @type {ResizeObserver | undefined} Watches the page's content: what loads in it moves its buttons. */
  #contenu;

  /** Pending end of a jump that does not reach its section (the bottom of the page, a finger on the screen). */
  #calme = 0;

  /** Stops waiting for the visitor's first gesture (the Chrome island at the top of the page). */
  #geste = null;

  /** @type {IntersectionObserver | undefined} Watches the pushes while the drawer is open (measure of the views). */
  #vues;

  /** Pending start of that watch, once the drawer has grown to its size. */
  #guet = 0;

  /** The pushes already measured as seen on this page. */
  #vus = new Set();

  get isOpen() {
    return this.hasAttribute('open');
  }

  connectedCallback() {
    super.connectedCallback();

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.addEventListener('keydown', this.#onKeydown, { signal });
    this.refs.tiroir.addEventListener('click', this.#onTiroirClick, { signal });
    // The labyrinth listens to swipes, wheel and arrow keys on the window: keep the drawer's to ourselves.
    for (const type of ['touchstart', 'touchmove', 'wheel']) {
      this.addEventListener(type, stopPropagation, { signal, passive: true });
    }
    this.refs.voile.addEventListener('touchmove', preventDefault, { signal, passive: false });

    window.addEventListener('resize', this.#schedule, { signal, passive: true });
    // Capture: Horizon scrolls `.page-wrapper` (≥ 990 px) and the product sheet scrolls its own layer.
    document.addEventListener('scroll', this.#schedule, { signal, passive: true, capture: true });
    window.addEventListener('load', this.#schedule, { signal, once: true });
    window.addEventListener('pageshow', this.#onPageshow, { signal });
    window.addEventListener('hashchange', this.#onHashchange, { signal });
    document.addEventListener('shopify:section:select', this.#onSectionSelect, { signal });
    document.addEventListener('shopify:section:deselect', this.#onSectionDeselect, { signal });
    document.addEventListener('shopify:block:select', this.#onBlockSelect, { signal });

    // The pushes' files: as soon as the drawer may open (a pointer over the button, the focus on it).
    for (const type of ['pointerenter', 'focus']) {
      this.refs.bouton.addEventListener(type, this.#loadPushes, { signal, once: true });
    }
    if (REDUCED_MOTION.matches) {
      for (const pause of this.querySelectorAll(PAUSE)) pause.setAttribute('aria-pressed', 'true');
    }

    // The home page switches between its dark and light themes (`data-v`), the product sheet opens as a
    // layer (`sdm-fiche-open`), the cookie banner is added to the body: all can change what is under us.
    this.#mutations = new MutationObserver(this.#schedule);
    this.#mutations.observe(document.documentElement, { attributes: true, attributeFilter: ['data-v', 'class'] });
    this.#mutations.observe(document.body, { childList: true, attributes: true, attributeFilter: ['class'] });

    // Its place and state before `data-pret`, which shows the button: it shows straight there (and not over a
    // button of the section the page opens on).
    this.#watchExperience();
    this.#followAnchor(anchorTarget(location.hash));
    this.#measure();
    this.#initGlass();

    // Shown when the page is ready for it: after the preloader, and in the experience with its interface.
    document.addEventListener('sdm:preloader-fin', this.#maybeShow, { signal, once: true });
    const lab = document.querySelector(LAB);
    if (lab) {
      this.#interface = new MutationObserver(this.#maybeShow);
      this.#interface.observe(lab, { attributes: true, attributeFilter: ['class'] });
    }
    this.#maybeShow();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.isOpen) this.#close(false);
    this.#abort?.abort();
    this.#geste?.abort();
    this.#mutations?.disconnect();
    this.#experience?.disconnect();
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
    clearTimeout(this.#bascule);
    clearTimeout(this.#arrivee);
    this.#arrivee = 0;
    this.#forgetAnchor();
    this.#interface?.disconnect();
    clearTimeout(this.#guet);
    this.#vues?.disconnect();
    document.documentElement.style.removeProperty('--sdm-burger-apercu');
    this.#apercu = 0;
  }

  /** Opens or closes the drawer (the button). */
  /** @param {MouseEvent} [event] - The button's click; from the keyboard (`detail` 0), the focus goes to the first row. */
  toggle(event) {
    if (this.isOpen) {
      this.#close(true);
    } else {
      this.#open(true, !event || event.detail === 0);
    }
  }

  /** Closes the drawer and gives the focus back to the button (the veil, Escape). */
  close() {
    this.#close(true);
  }

  /**
   * @param {boolean} modal - Keep the focus inside and stop the page from scrolling (not from the editor).
   * @param {boolean} [toFirst] - Focus the first row (opened from the keyboard); else the drawer itself, so a tap
   *   leaves no focus ring on a row (Safari rings an element focused by script after typing in a field).
   */
  #open(modal, toFirst = true) {
    if (this.isOpen) return;

    const { bouton, tiroir } = this.refs;

    this.#measure();
    this.setAttribute('open', '');
    tiroir.inert = false;
    bouton.setAttribute('aria-expanded', 'true');
    document.documentElement.classList.add('sdm-burger-open');

    this.#modal = modal;
    if (modal) {
      lockScroll(this);
      trapFocus(this);
      const first = toFirst ? tiroir.querySelector(/** @type {'a'} */ ('a[href], summary')) : null;
      (first ?? tiroir).focus({ preventScroll: true });
    }

    this.#loadPushes();
    this.#playPushes();
    this.#watchPushes();
    document.dispatchEvent(new CustomEvent('sdm:burger-open'));
  }

  /**
   * @param {boolean} restoreFocus - Give the focus back to the button if it was in the drawer.
   */
  #close(restoreFocus) {
    if (!this.isOpen) return;

    const { bouton, tiroir } = this.refs;
    const hadFocus = this.contains(document.activeElement) || document.activeElement === document.body;

    this.removeAttribute('open');
    tiroir.inert = true;
    bouton.setAttribute('aria-expanded', 'false');
    document.documentElement.classList.remove('sdm-burger-open');

    if (this.#modal) {
      removeTrapFocus();
      unlockScroll(this);
      this.#modal = false;
    }

    if (restoreFocus && hadFocus) bouton.focus({ preventScroll: true });

    this.#playPushes();
    this.#watchPushes();
    document.dispatchEvent(new CustomEvent('sdm:burger-close'));
    this.#place(this.#haut);
    this.#schedule();
  }

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (!this.isOpen) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.#close(true);
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.stopPropagation();
    }
  };

  /**
   * A link was followed (a page, the product sheet layer, the Zendesk window, a section of this page): the drawer
   * gets out of the way. A section of this very page is scrolled to, without loading the page again.
   * @param {MouseEvent} event
   */
  #onTiroirClick = (event) => {
    const pause = event.target instanceof Element ? event.target.closest(PAUSE) : null;
    if (pause) {
      pause.setAttribute('aria-pressed', String(pause.getAttribute('aria-pressed') !== 'true'));
      this.#playPushes();
      return;
    }

    const lien = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (!lien) return;

    if (lien.matches(PUSH)) measurePush('clic', lien);

    if (this.#isAssistance(lien) && openAssistance()) event.preventDefault();

    const ancre = event.defaultPrevented || !isPlainClick(event) ? null : anchorOnThisPage(lien);
    if (ancre) event.preventDefault();

    this.#close(false);
    if (ancre) {
      goToAnchor(ancre);
      this.#followAnchor(ancre.element);
    }
  };

  /** A link to a section of the page, anywhere on it (or back and forth between sections). */
  #onHashchange = () => {
    this.#followAnchor(anchorTarget(location.hash));
  };

  /**
   * A jump to a section of the page starts: until it is over, the button gives way to a button of the page that
   * will be under it there (see #givesWay).
   * @param {Element | null} element - The section's anchor.
   */
  #followAnchor(element) {
    if (!element) return;

    this.#forgetAnchor();
    this.#ancre = { element, arrivee: false, repos: null };
    this.#contenu = new ResizeObserver(this.#schedule);
    this.#contenu.observe(document.querySelector('main') ?? document.body);
    this.#schedule();
  }

  #forgetAnchor() {
    this.#ancre = null;
    this.#contenu?.disconnect();
    this.#contenu = undefined;
    clearTimeout(this.#calme);
  }

  /**
   * @param {Element} lien
   * @returns {boolean} Whether the link is the one set to open the Zendesk window.
   */
  #isAssistance(lien) {
    const assistance = this.dataset.assistance;
    const href = lien.getAttribute('href');
    if (!assistance || !href) return false;

    return new URL(href, location.href).pathname === new URL(assistance, location.href).pathname;
  }

  /**
   * Back from the back/forward cache with the drawer open.
   * @param {PageTransitionEvent} event
   */
  #onPageshow = (event) => {
    if (event.persisted) this.#close(false);
    this.#schedule();
  };

  /** @param {Event} event */
  #onSectionSelect = (event) => {
    if (event.target instanceof Node && event.target.contains(this)) this.#open(false);
  };

  /** @param {Event} event */
  #onSectionDeselect = (event) => {
    if (event.target instanceof Node && event.target.contains(this)) this.#close(false);
  };

  /** A push picked in the editor: the drawer opens on it. @param {Event} event */
  #onBlockSelect = (event) => {
    if (!(event.target instanceof Element) || !this.contains(event.target)) return;
    this.#open(false);
    event.target.scrollIntoView({ block: 'nearest' });
  };

  /** The pushes' pictures and films take their addresses (kept in data-* until then): once. */
  #loadPushes = () => {
    for (const media of this.querySelectorAll('.sdm-burger__push-media > [data-src]')) {
      if (!(media instanceof HTMLImageElement || media instanceof HTMLVideoElement)) continue;
      const { src, srcset } = media.dataset;
      if (media instanceof HTMLImageElement && srcset) media.srcset = srcset;
      if (src) media.src = src;
      delete media.dataset.src;
      delete media.dataset.srcset;
      if (media instanceof HTMLVideoElement) {
        media.addEventListener('playing', () => media.classList.add('is-lu'), { once: true });
      }
    }
  };

  /**
   * While the drawer is open, a push at least half in sight in it counts as seen, once a page. The watch starts once
   * the drawer has grown to its size (its rows still travel before): a push only crossed by the opening is not seen.
   */
  #watchPushes() {
    clearTimeout(this.#guet);
    this.#vues?.disconnect();
    this.#vues = undefined;

    const pushes = [...this.querySelectorAll(PUSH)].filter((push) => !this.#vus.has(push));
    if (!this.isOpen || !pushes.length || isEditor()) return;

    this.#guet = setTimeout(() => {
      if (!this.isOpen) return;
      this.#vues = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting || this.#vus.has(entry.target)) continue;
            this.#vus.add(entry.target);
            this.#vues?.unobserve(entry.target);
            measurePush('vue', entry.target);
          }
        },
        { root: this.querySelector('.sdm-burger__defile'), threshold: 0.5 },
      );
      for (const push of pushes) this.#vues.observe(push);
    }, VUE_APRES);
  }

  /** The films play while the drawer is open, unless paused (their button, or reduced motion). */
  #playPushes() {
    for (const pause of this.querySelectorAll(PAUSE)) {
      const video = pause.closest('.sdm-burger__push')?.querySelector('video');
      if (!video) continue;
      if (this.isOpen && pause.getAttribute('aria-pressed') !== 'true') {
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    }
  }

  /**
   * Shows the button (`data-pret`) once the page is ready for it: after the preloader if it is playing (once a
   * session); in the home page's experience, with its interface (the labyrinth's `sdm-hud-pret`, which brings the
   * header in too), a moment after it and slowly (`data-arrivee`).
   */
  #maybeShow = () => {
    if (this.hasAttribute('data-pret') || this.#arrivee) return;
    if (document.getElementById('sdm-preloader') && !(/** @type {any} */ (window).SDM_PRELOADER_FINI)) return;
    if (this.#waitsForGesture()) return;

    const lab = document.querySelector(LAB);
    if (!lab || !this.hasAttribute('data-experience')) {
      this.#show();
    } else if (lab.classList.contains('sdm-hud-pret')) {
      this.dataset.arrivee = 'experience';
      this.#arrivee = setTimeout(this.#show, ARRIVEE);
    }
  };

  /**
   * With the Chrome island at the top of the page, the first screen is left bare (JB, 26/10: « un premier écran ultra
   * propre et minimaliste »): the button waits for the visitor's first gesture — a scroll, a touch, a click, a key —
   * and then comes in slowly, as in the experience (`data-arrivee`).
   * @returns {boolean} Whether it is still waiting.
   */
  #waitsForGesture() {
    if (!document.querySelector(TETE)) return false;
    if (this.#geste?.signal.aborted) return false;
    if (this.#geste) return true;

    this.#geste = new AbortController();
    const { signal } = this.#geste;
    const go = () => {
      this.#geste?.abort();
      this.dataset.arrivee = 'experience';
      this.#maybeShow();
    };
    for (const type of GESTES) addEventListener(type, go, { signal, capture: true, passive: true });
    return true;
  }

  #show = () => {
    this.#interface?.disconnect();
    this.dataset.pret = '';
    this.#schedule();
  };

  /**
   * On the home page, puts the button under the header (`data-experience`) while the labyrinth's screen reaches
   * the bottom half of the window: it moves to the bottom once the experience is half scrolled past, as its
   * interface fades out.
   */
  #watchExperience() {
    const experience = document.querySelector(EXPERIENCE);
    if (!experience) return;

    this.#haut = fillsScreen(experience);
    this.toggleAttribute('data-experience', this.#haut);

    this.#experience = new IntersectionObserver(
      ([entry]) => {
        if (entry) this.#place(entry.isIntersecting);
      },
      { rootMargin: '-50% 0px 0px 0px' },
    );
    this.#experience.observe(experience);
  }

  /**
   * Moves the button under the header or to the bottom: it fades out (`data-sortie`), then in again at its new
   * place. Straight away while it is not shown yet; on close while the drawer is open.
   * @param {boolean} haut - Under the header.
   */
  #place(haut) {
    this.#haut = haut;
    if (this.isOpen || haut === this.hasAttribute('data-experience')) return;

    clearTimeout(this.#bascule);
    if (!this.hasAttribute('data-pret')) {
      // Out of the experience before its interface came in: no need to wait for it.
      this.toggleAttribute('data-experience', haut);
      this.#maybeShow();
      return;
    }

    this.toggleAttribute('data-sortie', true);
    this.#bascule = setTimeout(() => {
      this.toggleAttribute('data-experience', this.#haut);
      // Its state at the new place before it fades in: it may have to stay away there (a bar, a button).
      this.#measure();
      this.removeAttribute('data-sortie');
    }, SORTIE);
  }

  #schedule = () => {
    if (this.#frame) return;

    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.#measure();
    });
  };

  /**
   * Hides the button while a bar takes its place (and on a mobile product sheet, until the footer is under
   * it), then sets its tone from the background under it. Works from the layout (the fixed frame and its
   * margins), not from the button's box on screen: the button may be scaled to nothing (entrance, hidden).
   */
  #measure() {
    const { bouton } = this.refs;
    const cale = bouton.parentElement;
    const size = bouton.offsetHeight;

    if (!cale || !size) return;

    // Shopify's preview bar (theme previews only, never for shoppers) covers the bottom: sit above it. Set on
    // the root, so the footer's room for the button grows by it too.
    const barre = document.getElementById('PBarNextFrameWrapper')?.getBoundingClientRect();
    const apercu =
      barre && barre.height > 0 && barre.bottom >= innerHeight - 1 ? Math.round(innerHeight - barre.top) : 0;
    if (apercu !== this.#apercu) {
      this.#apercu = apercu;
      document.documentElement.style.setProperty('--sdm-burger-apercu', `${apercu}px`);
    }

    const frame = this.getBoundingClientRect();
    const margins = getComputedStyle(cale);
    // Top of the button: under the header (the frame's top margin), or at the bottom (bottom margin, safe area
    // and preview bar above the frame's bottom); and its centre.
    const top = this.hasAttribute('data-experience')
      ? frame.top + parseFloat(margins.paddingTop)
      : frame.bottom - parseFloat(margins.paddingBottom) - size;
    const bottom = top + size;
    // The button's centre, from the layout: mid-page, or where a page puts it (the cinema pages: on the left).
    const x = frame.left + bouton.offsetLeft + size / 2;

    const covered = [...document.querySelectorAll(OBSTACLES)].some((obstacle) => {
      const rect = obstacle.getBoundingClientRect();
      return (
        !this.contains(obstacle) &&
        rect.height > 0 &&
        rect.top < bottom + OBSTACLE_GAP &&
        rect.bottom > top &&
        rect.left < x + size / 2 &&
        rect.right > x - size / 2
      );
    });
    const waits = MOBILE.matches && isShown(document.querySelector(FICHE)) && !this.#footerAt(x, top - OBSTACLE_GAP);
    const givesWay = this.#givesWay(x, top, size);
    this.toggleAttribute('data-efface', (covered || waits || givesWay) && !this.isOpen);

    if (!this.isOpen) {
      const ton = this.#toneAt(x, top + size / 2);
      if (ton && this.dataset.ton !== ton) this.dataset.ton = ton;
    }
  }

  /**
   * After a jump to a section: whether a button or a link of the page will be under the button's place once the
   * jump is over. Its place there is read from the layout, from the section's top: the same from the start of the
   * jump to its end, whatever moves it for a moment (the glide, the blocks rising into place as they show, the
   * `sdm-reveal` of the SDM sections). Measured again while the page stays there, as the section changes size (an
   * image loading above a button pushes it down). Over once the visitor has scrolled away and none is there, or
   * once the button is under the header (the page went back to its top).
   *
   * @param {number} x - The button's centre.
   * @param {number} top - The button's top.
   * @param {number} size
   * @returns {boolean}
   */
  #givesWay(x, top, size) {
    const ancre = this.#ancre;
    if (!ancre) return false;

    // Not a section of the page (hidden, or in a layer over it): nothing to watch.
    const { element } = ancre;
    const repere = layoutTop(element);
    if (!repere?.racine.contains(document.querySelector('main') ?? document.body)) {
      this.#forgetAnchor();
      return false;
    }

    // Where the section's top rests at the end of the jump: under the header, or where the jump stopped short.
    const actuel = element.getBoundingClientRect().top;
    const cible = parseFloat(getComputedStyle(element).scrollMarginTop) || 0;
    if (!ancre.arrivee) {
      if (Math.abs(actuel - cible) < 2) {
        ancre.arrivee = true;
      } else {
        // Still for a moment: the jump stopped short (the bottom of the page, a finger on the screen). Still indeed:
        // a busy page may not have measured for a while as the glide goes on.
        clearTimeout(this.#calme);
        this.#calme = setTimeout(() => {
          if (this.#ancre !== ancre) return;
          if (Math.abs(element.getBoundingClientRect().top - actuel) < 1) ancre.arrivee = true;
          this.#schedule();
        }, CALME);
      }
    }
    if (ancre.arrivee) ancre.repos ??= actuel;

    // Under the header, nothing to give way to. Over if the page is back at the experience (the preloader takes
    // it there); otherwise the button is about to move down (the jump may end before it does, on a slow page).
    if (this.hasAttribute('data-experience')) {
      if (ancre.arrivee && fillsScreen(document.querySelector(EXPERIENCE))) this.#forgetAnchor();
      return false;
    }

    const section = ancre.arrivee ? actuel : cible;
    const sous = [...(document.querySelector('main') ?? document).querySelectorAll(BOUTONS)].some((bouton) => {
      if (this.contains(bouton)) return false;

      // In the same layout as the section's top (not in a fixed layer), as tall as two buttons at most.
      const position = layoutTop(bouton);
      const hauteur = bouton instanceof HTMLElement ? bouton.offsetHeight : 0;
      if (position?.racine !== repere.racine || !hauteur || hauteur > size * 2) return false;

      const haut = section + position.top - repere.top;
      const rect = bouton.getBoundingClientRect();
      return (
        haut < top + size + OBSTACLE_GAP &&
        haut + hauteur > top - OBSTACLE_GAP &&
        rect.left < x + size / 2 + OBSTACLE_GAP &&
        rect.right > x - size / 2 - OBSTACLE_GAP
      );
    });

    if (!sous && ancre.repos !== null && Math.abs(actuel - ancre.repos) > BOUGE) this.#forgetAnchor();
    return sous;
  }

  /**
   * @param {number} x
   * @param {number} y
   * @returns {boolean} Whether the footer is what shows at a point (the burger aside): the bottom of the page.
   */
  #footerAt(x, y) {
    const element = document.elementsFromPoint(x, y).find((element) => !this.contains(element));
    return !!element?.closest(FOOTER);
  }

  /**
   * The tone of the first thing painted under a point: the labyrinth's canvas follows the home page's theme,
   * anything else its background colour, if it is opaque enough. Null when nothing tells (keep the tone).
   *
   * @param {number} x
   * @param {number} y
   * @returns {'clair' | 'sombre' | null}
   */
  #toneAt(x, y) {
    for (const element of document.elementsFromPoint(x, y)) {
      if (this.contains(element)) continue;

      if (element.matches('canvas.sdm-stage')) {
        return document.documentElement.dataset.v === 'white' ? 'clair' : 'sombre';
      }

      // The Chrome island's opening (assets/sdm-chrome-huit.js): its scene lets the pointer through, so the island's
      // dark background is found first; while the light labyrinth fills the screen, the root says so.
      if (element.matches('.sdm-chrome--huit') && document.documentElement.classList.contains('sdm-huit-clair')) {
        return 'clair';
      }

      const color = parseColor(getComputedStyle(element).backgroundColor);
      if (color && (color[3] ?? 1) >= 0.5) return luminance(color) < DARK ? 'sombre' : 'clair';
    }

    return null;
  }

  async #initGlass() {
    const { bouton, tiroir, defs } = this.refs;

    if (this.dataset.refraction === 'false' || !defs) return;

    try {
      // @ts-ignore - resolved by the theme's import map (snippets/sdm-importmap.liquid)
      const { createGlass, GLASS_DEFAULTS } = await import('sdm/glass');

      if (!this.isConnected) return;

      // Not `applyStyleVars()`: it writes the home page's glass variables on the root element.
      const glass = createGlass({ defs, params: { ...structuredClone(GLASS_DEFAULTS), ...GLASS } });
      glass.register(bouton);
      glass.register(tiroir);
    } catch {
      // No sdm/glass in the import map: the CSS blur stays.
    }
  }
}

/** The labyrinth's glass on the home page (assets/sdm-labyrinthe.js), so both read as one material. */
const GLASS = { bezel: 18, depth: 40, chroma: 0.18, blur: 0.8, tint: 0.05, rim: 0.7, spec: 0.28 };

/** The home page's labyrinth (sections/sdm-labyrinthe.liquid), which marks its interface ready (`sdm-hud-pret`). */
const LAB = '[data-sdm-lab]';

/** The labyrinth's screen, where the button sits under the header. */
const EXPERIENCE = `${LAB} .sdm-hero`;

/** How long after the experience's interface the button comes in, in ms: once the header has mostly shown. */
const ARRIVEE = 800;

/** The Chrome island opening the page (sections/sdm-ilot.liquid): the button waits there for a first gesture. */
const TETE = '.sdm-huit--tete';

/** What counts as the visitor's first gesture there. */
const GESTES = ['scroll', 'wheel', 'touchstart', 'pointerdown', 'keydown'];

/** A push film's pause button (sections/sdm-burger.liquid): `aria-pressed` true while paused. */
const PAUSE = '.sdm-burger__push-pause';

/** A push's link, which carries what the measure reports (`data-push-*`, sections/sdm-burger.liquid). */
const PUSH = '.sdm-burger__push-lien';

/** How long the drawer takes to grow to its size, in ms (the CSS: 0.6 s), before a push in sight counts as seen. */
const VUE_APRES = 650;

/** Bars that can take the button's place: the product sheet's purchase bar, the cookie banner. */
const OBSTACLES = '.sdm-fiche-actions, #shopify-pc__banner, [data-sdm-burger-obstacle]';

/** Room kept between the button and a bar under it, in px. */
const OBSTACLE_GAP = 12;

/**
 * What the button does not cover at the end of a jump to a section: the page's buttons and links, as tall as two
 * of it at most (not a whole product card, which shows around it).
 */
const BOUTONS = 'a[href], button, [role="button"], summary, input:not([type="hidden"]), select, textarea';

/** How long the page stays still before a jump that does not reach its section counts as over, in ms. */
const CALME = 250;

/** How far the visitor scrolls from where a jump left the page before the button stops watching for it, in px. */
const BOUGE = 40;

/** A product sheet, as a page or as the home page's layer: on mobile the button waits for the footer. */
const FICHE = '.sdm-fiche';

/** The footer group's sections (the footer, its policies band, the newsletter). */
const FOOTER = '.shopify-section-group-footer-group';

/** Below the button's breakpoint (sections/sdm-burger.liquid). */
const MOBILE = matchMedia('(max-width: 749px)');

/** How long the button is given to fade out before changing places, in ms (the CSS `data-sortie` state: 250). */
const SORTIE = 300;

/** Below this relative luminance a background counts as dark: where light ink starts to contrast more. */
const DARK = 0.23;

/** Less motion asked: a jump to the section rather than a glide. */
const REDUCED_MOTION = matchMedia('(prefers-reduced-motion: reduce)');

/**
 * Reports a push seen or followed to the measures the site already runs, each only if it is there (consent is theirs):
 * - Google Analytics 4, as an internal promotion — `view_promotion` and `select_promotion`, its "Promotions" report:
 *   the push's block as promotion (id and title), image or film as creative, its place in the grid as slot, and
 *   where it leads as the item (the report leaves out events without one);
 * - Shopify's customer events, `sdm_push_vue` and `sdm_push_clic` with the same data and the theme, for a custom
 *   pixel (Settings › Customer events) or an app;
 * - Clarity, on a click: the event `sdm_push_clic` and the tag `sdm_push`, to find those sessions.
 * Nothing from the theme editor.
 * @param {'vue' | 'clic'} type
 * @param {HTMLElement} lien - The push's link.
 */
function measurePush(type, lien) {
  if (isEditor()) return;

  const w = /** @type {any} */ (window);
  const { pushId = '', pushTitre = '', pushMedia = '', pushPlace = '' } = lien.dataset;
  const href = lien.getAttribute('href');
  const url = href ? new URL(href, location.href) : null;
  const cible = url ? (url.origin === location.origin ? '' : url.host) + url.pathname + url.hash : pushId;
  const promotion = {
    promotion_id: pushId,
    promotion_name: pushTitre,
    creative_name: pushMedia,
    creative_slot: `menu_${pushPlace}`,
  };

  try {
    if (typeof w.gtag === 'function') {
      w.gtag('event', type === 'clic' ? 'select_promotion' : 'view_promotion', {
        ...promotion,
        items: [{ item_id: cible, item_name: pushTitre, index: Number(pushPlace) || 0 }],
      });
    }
  } catch {}

  try {
    w.Shopify?.analytics?.publish?.(`sdm_push_${type}`, { ...promotion, lien: cible, theme: w.Shopify?.theme?.name ?? '' });
  } catch {}

  if (type === 'clic') {
    try {
      if (typeof w.clarity === 'function') {
        w.clarity('event', 'sdm_push_clic');
        w.clarity('set', 'sdm_push', pushTitre);
      }
    } catch {}
  }
}

/** @returns {boolean} Whether the page is shown in the theme editor. */
function isEditor() {
  return !!(/** @type {any} */ (window).Shopify?.designMode);
}

/**
 * @param {MouseEvent} event
 * @returns {boolean} Whether the click follows the link in place (not in a new tab or window).
 */
function isPlainClick(event) {
  return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
}

/**
 * @param {Element} lien
 * @returns {{ element: Element, hash: string } | null} The section of this very page the link points at, when
 *   it is an anchor of it (snippets/sdm-ancre.liquid): same path, and the same site whether the address is
 *   written with "www." or not (menus often hold full addresses, which the browser would load again).
 */
function anchorOnThisPage(lien) {
  const href = lien.getAttribute('href');
  if (!href || lien.getAttribute('target') === '_blank') return null;

  const url = new URL(href, location.href);
  const site = (/** @type {string} */ host) => host.replace(/^www\./, '');
  if (!url.hash || url.pathname !== location.pathname || site(url.hostname) !== site(location.hostname)) return null;

  const element = anchorTarget(url.hash);
  return element ? { element, hash: url.hash } : null;
}

/**
 * @param {string} hash - `#` and an anchor, or nothing.
 * @returns {Element | null} The element of the page that the anchor names.
 */
function anchorTarget(hash) {
  if (hash.length < 2) return null;

  try {
    return document.getElementById(decodeURIComponent(hash.slice(1)));
  } catch {
    return null;
  }
}

/**
 * Scrolls to a section of this page as following its anchor would, without loading the page again: the address
 * takes the anchor and keeps the rest (the theme preview's query, for one).
 * @param {{ element: Element, hash: string }} ancre
 */
function goToAnchor({ element, hash }) {
  if (location.hash !== hash) history.pushState(null, '', hash);
  element.scrollIntoView({ behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth', block: 'start' });
}

/**
 * Opens the Zendesk window (Web Widget). Calls made before the widget has loaded are queued by its snippet.
 * @returns {boolean} Whether Zendesk took it; otherwise the link leads to its page.
 */
function openAssistance() {
  const zE = /** @type {any} */ (window).zE;
  if (typeof zE !== 'function') return false;

  try {
    zE('webWidget', 'open');
    return true;
  } catch {
    return false;
  }
}

/**
 * On mobile the Zendesk window opens full screen and fixes the body (`position: fixed; inset: 0`), which sends the
 * page back to its top while it is open: on the home page the experience comes back under it, and shows for a
 * moment when it closes, before Zendesk scrolls back (as if the page reloaded). Keeps the page where it was:
 * while Zendesk holds the body, it is offset by the scroll position, which is given back as soon as the body
 * is released, before anything is painted. Whoever opens the window (the drawer's Contact, the footer's).
 */
function keepScrollUnderZendesk() {
  const body = document.body;
  let y = scrollY;
  /** @type {number | null} */
  let held = null;

  // On the window, in capture: ahead of every other scroll listener. While Zendesk holds the body, its jump to
  // the top is nobody's business (the labyrinth would wake up and the header change colours, then back).
  addEventListener(
    'scroll',
    (event) => {
      if (held !== null) event.stopImmediatePropagation();
      else if (body.style.position !== 'fixed') y = scrollY;
    },
    { capture: true, passive: true },
  );

  new MutationObserver(() => {
    if (body.style.position === 'fixed') {
      if (held === null && typeof (/** @type {any} */ (window).zE) !== 'function') return;
      held ??= y;
      if (body.style.top !== `${-held}px`) body.style.top = `${-held}px`;
    } else if (held !== null) {
      body.style.removeProperty('top');
      scrollTo({ top: held, behavior: 'instant' });
      held = null;
    }
  }).observe(body, { attributes: true, attributeFilter: ['style'] });
}

/**
 * An element's top in the layout: as laid out, before any transform (a block rising into place as it shows) and
 * whatever the scroll, from the top of the box it is laid out in: the page, or a fixed layer.
 * @param {Element} element
 * @returns {{ top: number, racine: Element } | null} Null for an element that is not an HTML one.
 */
function layoutTop(element) {
  if (!(element instanceof HTMLElement)) return null;

  let top = 0;
  /** @type {HTMLElement} */
  let box = element;
  for (;;) {
    top += box.offsetTop;
    const parent = box.offsetParent;
    if (!(parent instanceof HTMLElement)) return { top, racine: box };
    box = parent;
  }
}

/**
 * @param {Element | null} experience - The labyrinth's screen.
 * @returns {boolean} Whether it reaches the bottom half of the window: the button's place is under the header.
 */
function fillsScreen(experience) {
  if (!experience) return false;

  const box = experience.getBoundingClientRect();
  return box.bottom > innerHeight / 2 && box.top < innerHeight;
}

/**
 * @param {Element | null} element
 * @returns {boolean} Whether the element is rendered (not `display: none`, nor in a closed dialog).
 */
function isShown(element) {
  return !!element && element.getClientRects().length > 0;
}

/** @param {Event} event */
function stopPropagation(event) {
  event.stopPropagation();
}

/** @param {Event} event */
function preventDefault(event) {
  event.preventDefault();
}

/**
 * @param {string} value - A computed colour: `rgb()`, `rgba()` or `color(srgb …)`.
 * @returns {number[] | null} Red, green and blue from 0 to 1, and alpha.
 */
function parseColor(value) {
  const rgb = value.match(/^rgba?\(([\d.]+),\s*([\d.]+),\s*([\d.]+)(?:,\s*([\d.]+))?\)$/);
  if (rgb) return [rgb[1], rgb[2], rgb[3]].map((c) => Number(c) / 255).concat(Number(rgb[4] ?? 1));

  const srgb = value.match(/^color\(srgb ([\d.]+) ([\d.]+) ([\d.]+)(?: \/ ([\d.]+))?\)$/);
  if (srgb) return [srgb[1], srgb[2], srgb[3], srgb[4] ?? 1].map(Number);

  return null;
}

/**
 * @param {number[]} color - Red, green and blue from 0 to 1.
 * @returns {number} Relative luminance (WCAG).
 */
function luminance([r = 0, g = 0, b = 0]) {
  const linear = (/** @type {number} */ c) => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
  return 0.2126 * linear(r) + 0.7152 * linear(g) + 0.0722 * linear(b);
}

if (!customElements.get('sdm-burger')) {
  customElements.define('sdm-burger', SdmBurger);
  keepScrollUnderZendesk();
}
